import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';

interface SalaryStructure {
  id: string;
  code: string;
  structure_name: string;
  basic_salary: number;
  ctc: number;
  is_current: boolean;
}

const EMPTY_FORM = {
  code: '',
  structureName: '',
  basicSalary: 0,
  grossSalary: 0,
  netSalary: 0,
  ctc: 0,
  effectiveFrom: '',
};

export default function SalaryStructures() {
  const [structures, setStructures] = useState<SalaryStructure[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<SalaryStructure | null>(null);

  useEffect(() => {
    fetchStructures();
  }, []);

  const fetchStructures = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payroll/structures');
      setStructures(response.data.structures || []);
    } catch (error) {
      console.error('Failed to fetch salary structures:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditForm = (struct: SalaryStructure) => {
    setEditingId(struct.id);
    setFormData({
      ...EMPTY_FORM,
      code: struct.code,
      structureName: struct.structure_name,
      basicSalary: struct.basic_salary,
      ctc: struct.ctc,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/payroll/structures/${editingId}`, formData);
      } else {
        await api.post('/payroll/structures', formData);
      }
      setFormData(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);
      fetchStructures();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save salary structure');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/payroll/structures/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchStructures();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to deactivate structure');
    }
  };

  const columns: Column<SalaryStructure>[] = [
    { key: 'code', header: 'Code' },
    { key: 'structure_name', header: 'Structure Name', sortable: true },
    { key: 'basic_salary', header: 'Basic Salary', render: (row) => `$${Number(row.basic_salary).toLocaleString()}` },
    { key: 'ctc', header: 'CTC', render: (row) => `$${Number(row.ctc).toLocaleString()}` },
    {
      key: 'is_current',
      header: 'Status',
      render: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${row.is_current ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {row.is_current ? 'Current' : 'Archived'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-2">
          <button onClick={() => openEditForm(row)} className="text-blue-600 hover:underline text-sm">
            Edit
          </button>
          <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline text-sm">
            Deactivate
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Salary Structures</h1>
        <Button onClick={openCreateForm}>Create Structure</Button>
      </div>

      <Table columns={columns} rows={structures} loading={loading} emptyMessage="No salary structures yet" />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Structure' : 'Create Structure'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Structure Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
              disabled={!!editingId}
            />
            <Input
              label="Structure Name"
              value={formData.structureName}
              onChange={(e) => setFormData({ ...formData, structureName: e.target.value })}
              required
            />
            <Input
              label="Basic Salary"
              type="number"
              value={formData.basicSalary}
              onChange={(e) => setFormData({ ...formData, basicSalary: parseFloat(e.target.value) })}
              required
            />
            <Input
              label="Gross Salary"
              type="number"
              value={formData.grossSalary}
              onChange={(e) => setFormData({ ...formData, grossSalary: parseFloat(e.target.value) })}
            />
            <Input
              label="Net Salary"
              type="number"
              value={formData.netSalary}
              onChange={(e) => setFormData({ ...formData, netSalary: parseFloat(e.target.value) })}
            />
            <Input
              label="CTC"
              type="number"
              value={formData.ctc}
              onChange={(e) => setFormData({ ...formData, ctc: parseFloat(e.target.value) })}
            />
            {!editingId && (
              <Input
                label="Effective From"
                type="date"
                value={formData.effectiveFrom}
                onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
                className="col-span-2"
              />
            )}
          </div>
          <Button type="submit" className="w-full">
            {editingId ? 'Save Changes' : 'Create Structure'}
          </Button>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Deactivate Structure"
        message={`Deactivate "${deleteTarget?.structure_name}"?`}
        confirmLabel="Deactivate"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
